import { Container, Graphics } from "pixi.js";
import { createFeatheredEllipse } from "../lib/soft-shape";
import type { Point, Token } from "../types";

interface FlyParticle {
  graphic: Graphics;
  phase: number;
  speed: number;
  orbitX: number;
  orbitY: number;
  wobble: number;
}

function seedFrom(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index); hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function randomGenerator(seed: number) {
  let value = seed || 1;
  return () => {
    value += 0x6d2b79f5;
    let result = value; result = Math.imul(result ^ result >>> 15, result | 1);
    result ^= result + Math.imul(result ^ result >>> 7, result | 61);
    return ((result ^ result >>> 14) >>> 0) / 4294967296;
  };
}

/** Small deterministic Pixi/WebGL prop for carrion, refuse, and stagnant areas. */
export function createFlySwarm(token: Token, position: Point, radius: number, alpha: number) {
  const swarm = new Container(); swarm.position.set(position.x, position.y); swarm.alpha = alpha;
  const random = randomGenerator(seedFrom(token.id)); const particles: FlyParticle[] = [];
  const count = Math.max(14, Math.min(30, Math.round(12 + token.size * 7)));
  swarm.addChild(createFeatheredEllipse({ x: radius * 0.08, y: radius * 0.12, radiusX: radius * 1.12, radiusY: radius * 0.76, color: "#11110f", alpha: 0.11, feather: 0.68, layers: 10 }));

  for (let index = 0; index < count; index += 1) {
    const fly = new Graphics(); const bodyLength = Math.max(1.4, radius * (0.028 + random() * 0.014)); const bodyWidth = Math.max(0.65, bodyLength * 0.38);
    fly.ellipse(-bodyLength * 0.08, -bodyWidth * 0.58, bodyLength * 0.42, bodyWidth * 0.46).fill({ color: "#d8d2c4", alpha: 0.34 });
    fly.ellipse(-bodyLength * 0.08, bodyWidth * 0.58, bodyLength * 0.42, bodyWidth * 0.46).fill({ color: "#d8d2c4", alpha: 0.30 });
    fly.ellipse(0, 0, bodyLength * 0.52, bodyWidth).fill({ color: index % 4 === 0 ? "#292822" : "#11110f", alpha: 0.96 });
    fly.circle(bodyLength * 0.42, 0, bodyWidth * 0.42).fill({ color: "#080807", alpha: 1 });
    swarm.addChild(fly);
    particles.push({
      graphic: fly,
      phase: random() * Math.PI * 2,
      speed: 0.75 + random() * 1.20,
      orbitX: radius * (0.34 + random() * 0.88),
      orbitY: radius * (0.22 + random() * 0.56),
      wobble: radius * (0.035 + random() * 0.10),
    });
  }

  let elapsed = seedFrom(token.id) % 997 / 997 * Math.PI * 2;
  const positionParticles = () => {
    for (let index = 0; index < particles.length; index += 1) {
      const particle = particles[index]; const angle = elapsed * particle.speed + particle.phase;
      const rapid = elapsed * (2.7 + index % 5 * 0.31) + particle.phase * 1.73;
      const x = Math.cos(angle) * particle.orbitX + Math.sin(rapid) * particle.wobble;
      const y = Math.sin(angle * 1.19) * particle.orbitY + Math.cos(rapid * 1.31) * particle.wobble * 0.72;
      particle.graphic.position.set(x, y);
      particle.graphic.rotation = Math.atan2(Math.cos(angle * 1.19) * particle.orbitY, -Math.sin(angle) * particle.orbitX) + Math.sin(rapid) * 0.28;
      const wingFlicker = 0.82 + Math.sin(rapid * 4.4) * 0.18; particle.graphic.scale.set(1, wingFlicker);
    }
  };
  positionParticles();

  const animate = (deltaMs: number) => {
    elapsed += Math.min(deltaMs, 80) / 1000; positionParticles();
  };
  return { swarm, animate };
}
